import {React, useRef, useState, useEffect} from 'react';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {H2Neutral50, H3Neutral50, PNeutral50, PNoMargin} from '@/typography';
import HubspotForm from '@/ui/hubspotform';
import FormsHandler from '@/formshandler/index';
import {withBgOrange50, withNeutral50} from '@/hocs/withColors';
import {SectionRoiCalculatorBg, SectionPadding, SectionSpacedTopLgToDouble} from '@/section';
import {withMarginBottom2xl, withMarginBottomHuge} from '@/hocs/withSpacing';
import {sendDataLayerEvent} from 'utils/analytics';
import StatsCols from '@/content/roi-calculator/saving-stats/saving-cols';
import styles from './roi-calculator.module.scss';


const ContainerROI = withBgOrange50(Container);
const DivSpace = withMarginBottom2xl('div');
const PSmallNeutral50NoMargin = withMarginBottomHuge(withNeutral50(PNoMargin));

const RoiStatsAndCalculator = props => {
  const ROIsectionRef = useRef(null);

  const [submitted, setSubmitted] = useState(false);

  const minValues = {
    annual_revenue: 50000,
    number_of_employees: 1,
    current_time_to_fill: 10,
    avg_employee_salary: 20000,
    turnover_rate_yearly: 5,
    expected_number_of_new_hires: 1,
    avg_recruiter_salary: 20000,
    number_of_recruiters: 1,
    job_board_costs: 5,
    cost_of_third_party_tools: 2000,
    cost_of_recruiting_agencies: 0
  };

  const formFieldsKeys = [
    'annual_revenue',
    'number_of_employees',
    'current_time_to_fill',
    'avg_employee_salary',
    'turnover_rate_yearly',
    'expected_number_of_new_hires',
    'avg_recruiter_salary',
    'number_of_recruiters',
    'job_board_costs',
    'cost_of_third_party_tools',
    'cost_of_recruiting_agencies'
  ];

  let formData = {};

  const resultsValues = {};

  const cost_of_workable = 6000;

  useEffect(() => {
    if (submitted && ROIsectionRef?.current) {
      ROIsectionRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [submitted]);

  const calculateTimeToHire = ({current_time_to_fill, annual_revenue, number_of_employees}) => {
    if (current_time_to_fill < 40) {
      return 0;
    } 
      return Math.round((current_time_to_fill - 40) * (annual_revenue / number_of_employees / 220));
    
  };

  const calculateQualityOfHire = ({
    avg_employee_salary,
    number_of_employees,
    turnover_rate_yearly,
    expected_number_of_new_hires
  }) => Math.round(
      avg_employee_salary *
        0.2 *
        (number_of_employees * (turnover_rate_yearly / 100) + expected_number_of_new_hires)
    );

  const calculateProductivitySavings = ({avg_recruiter_salary, number_of_recruiters}) => Math.round((avg_recruiter_salary / 220) * 0.3 * 220 * number_of_recruiters);

  const calculateCostConsolidation = ({
    job_board_costs,
    cost_of_third_party_tools,
    cost_of_recruiting_agencies
  }) => Math.round(job_board_costs + cost_of_third_party_tools + cost_of_recruiting_agencies);

  const calculateTotal = ({
    resultOfReducedTimeToHire,
    resultOfImprovedQualityOfTime,
    resultOfIncreasedProductivity,
    resultOfRemovalAgency
  }) => Math.round(
      resultOfReducedTimeToHire +
        resultOfImprovedQualityOfTime +
        resultOfIncreasedProductivity +
        resultOfRemovalAgency
    );

  const calculateROI = total => parseFloat(((total - cost_of_workable) / cost_of_workable).toFixed(1));

  const populateHiddenFields = (
    {
      resultOfReducedTimeToHire,
      resultOfImprovedQualityOfTime,
      resultOfIncreasedProductivity,
      resultOfRemovalAgency
    },
    total,
    roi
  ) => {
    document.querySelector('input[name="result_of_reduced_time_to_hire"]').value =
      resultOfReducedTimeToHire;
    document.querySelector('input[name="result_of_improved_quality_of_time"]').value =
      resultOfImprovedQualityOfTime;
    document.querySelector('input[name="result_of_increased_productivity"]').value =
      resultOfIncreasedProductivity;
    document.querySelector('input[name="result_of_removal_agency"]').value = resultOfRemovalAgency;
    document.querySelector('input[name="total"]').value = total;
    document.querySelector('input[name="roi"]').value = roi;
  };

  const handleOnFormReady = () => {
    const formFieldsValues = Object.fromEntries(
      formFieldsKeys.map(key => [
        key,
        Math.abs(parseFloat(document.querySelector(`input[name=${ key }]`).value))
          ? Math.abs(parseFloat(document.querySelector(`input[name=${ key }]`).value))
          : minValues[key]
      ])
    );
    resultsValues.resultOfReducedTimeToHire = calculateTimeToHire(formFieldsValues);

    resultsValues.resultOfImprovedQualityOfTime = calculateQualityOfHire(formFieldsValues);

    resultsValues.resultOfIncreasedProductivity = calculateProductivitySavings(formFieldsValues);

    resultsValues.resultOfRemovalAgency = calculateCostConsolidation(formFieldsValues);

    const total = calculateTotal(resultsValues);

    const roi = calculateROI(total);

    populateHiddenFields(resultsValues, total, roi);
  };

  return (
    <SectionRoiCalculatorBg>
      <Container>
        <Grid fluid>
          <Row center="xs">
            <Col xs={12}>
              <DivSpace>
                <H3Neutral50>How much you could save</H3Neutral50>
              </DivSpace>
            </Col>
          </Row>
          <StatsCols />
          <Row center="xs">
            <Col xs={12}>
              <PSmallNeutral50NoMargin>
                *Estimations based on Workable customer data and user feedback.
              </PSmallNeutral50NoMargin>
            </Col>
          </Row>
        </Grid>
      </Container>
      <div ref={ROIsectionRef}>
        <SectionPadding id="formContainer">
          <Container>
            <Grid fluid>
              <Row center="xs">
                <Col xs={12}>
                  <H2Neutral50>ATS ROI Calculator</H2Neutral50>
                </Col>
              </Row>
              <Row center="xs">
                <Col xs={12}>
                  <PNeutral50>
                    Complete the fields below to see a detailed breakdown of how much you could save
                    by using Workable.
                  </PNeutral50>
                </Col>
              </Row>
              <ContainerROI className={styles['container-border']}>
                <SectionSpacedTopLgToDouble>
                  <Row>
                    <Col xs={12} md={10} mdOffset={1}>
                      <div
                        id="roi-calculator-form"
                        className="hs-form form-wrapper form-roi">
                        <HubspotForm
                          portalId={process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID}
                          formId={process.env.NEXT_PUBLIC_CALCULATOR_FORM_ID}
                          css=""
                          onFormError={FormsHandler.handleFormError}
                          onFormReady={form => {
                            FormsHandler.handleFormReady();

                            form.addEventListener('submit', e => {
                              e.preventDefault();
                              handleOnFormReady();
                              const data = new FormData(e.target);
                              formData = Object.fromEntries([...data.entries()]);
                            });
                          }}
                          onFormSubmitted={() => {
                            setSubmitted(true);
                            sendDataLayerEvent('roiCalculationFormSubmit', formData);
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </SectionSpacedTopLgToDouble>
              </ContainerROI>
            </Grid>
          </Container>
        </SectionPadding>
      </div>
    </SectionRoiCalculatorBg>
  );
};

export default RoiStatsAndCalculator;
